// ---------------------------------------------------------------------------
// publish-readiness-verdict — publish the publish-readiness pre-flight into
// the canonical verdict store, and reflux the server gate's verdict (F25).
//
// Before F25, `evaluateStudioPublishReadiness` was a 5th verdict DIALECT: the
// publish/deploy surfaces re-derived it inline and it never entered the
// canonical verdict store. So the surface could show a green 'ready' while the
// SERVER quality gate inside publishProject rejected with 'Publishing blocked'
// — the app-health pill and the publish readiness disagreed, and a creator
// trusted a green the server then contradicted.
//
// HONEST MODEL — estimate vs authoritative (the redesign):
//   The SURFACE pre-flight is an ESTIMATE: it runs only the client-visible
//   readiness gates over the in-memory bundle and OMITS the server quality gate
//   (per-block score / cross-block chain integrity, which depend on
//   DB-persisted scores). The SERVER gate inside publishProject is the
//   AUTHORITATIVE answer. So the two signals are NOT symmetrical:
//
//     - surface, not-yet-ready  → 'review' (AMBER, "not ship-ready yet"). A
//       still-building draft is NOT broken — it is an informational estimate
//       that the app is not finished, so it must NOT pin the global pill RED
//       (R1). 'fix' (red) is reserved for the authoritative server reflux.
//     - surface, ready          → 'ok' (a real client evaluation contributes to
//       the green vote), but the copy frames it as a PRE-FLIGHT estimate
//       ("client checks pass — the server confirms the rest on publish") because
//       the surface omits the server quality gate (L5-C5/ST-1: green can still
//       precede a server reject until an attempt refluxes).
//     - server reflux, blocked  → 'fix' (RED, authoritative). This is the only
//       producer that escalates the pill to red.
//
// SERVER REFLUX STICKS over a surface re-publish (CA-1 = L5-C4 — the real
//   closure): the surface effect re-renders constantly (the gate is a fresh
//   object every render). After the server reflux writes 'fix', a passive
//   surface re-evaluation with an UNCHANGED gate must NOT overwrite it with a
//   lenient 'review'/'ok' — otherwise the pill re-greens and the authoritative
//   rejection is hidden. We make the server reflux STICKY: it sets a
//   server-blocked flag that the surface publisher honors, cleared only when the
//   underlying gate inputs GENUINELY change. "Genuine change" is signalled by a
//   content-mutation epoch (`markPublishReadinessInputsChanged`, wired to the
//   studio-store content subscriber that already markStale's this source). So a
//   bare remount/re-render cannot move the pill off the server 'fix'; only a
//   real content edit (→ new evaluation) can.
//
// RUN-TOKEN monotonicity (L5-C1 = CA-3): the token counter is MONOTONIC and is
//   never reset to 0 — a project switch resets the change-guard memo and the
//   sticky flag, but LEAVES the token counter increasing, so a slow stale
//   rejection captured under project A's token can never collide with a token
//   re-issued for project B. `refluxServerGateRejection` additionally carries
//   the project id so a cross-project reflux is rejected even if a token ever
//   matched.
//
// Contract:
//   - UNSCOPED (scope:null) — publish readiness is a WHOLE-APP pre-flight.
//   - SAFE TO CALL PER-RENDER — `publishReadinessVerdict` WRITES ONLY ON CHANGE
//     (memoizes the last published signature) so a naive publish-on-every-render
//     no-ops; the change guard is the effect-loop backstop the verdict store
//     Lane-1's actionflow-edit source depends on.
//   - NEVER throws — humanization and publish are best-effort.
// ---------------------------------------------------------------------------

import {
  useValidationVerdictStore,
  type PublishVerdictInput,
  type VerdictEntry,
} from './verdict-store'
import {
  humanizePublishReason,
} from '@/shell/ide-v2/components/publish-gate-copy'
import type { LocaleCode } from '@/pages/cogo-marketing/i18n/locales'

const SOURCE = 'publish-readiness' as const
const LABEL = 'Publish readiness'

/** The minimum the publisher needs from a StudioPublishReadinessGate. */
export interface PublishReadinessGateLike {
  publishReady: boolean
  reasons: string[]
}

/** Where a published signature came from — surface estimate vs server gate. */
type VerdictOrigin = 'surface' | 'server'

/** Stable signature of the last published verdict — the change guard. */
interface PublishedSignature {
  /**
   * 'review' = surface estimate "not ship-ready yet" (amber, NOT broken).
   * 'ok'     = surface estimate "client checks pass" (green pre-flight).
   * 'fix'    = authoritative server reflux "Publishing blocked" (red).
   */
  verdict: 'ok' | 'review' | 'fix'
  origin: VerdictOrigin
  locale: LocaleCode
  worstCheck: string | null
  /** The raw reason that produced worstCheck — distinguishes equal titles. */
  rawReason: string | null
}

let currentRunToken = 0
let lastPublished: PublishedSignature | null = null

// --- server-reflux sticky flag (CA-1 = L5-C4) ------------------------------
// When the server gate refluxes 'fix', it pins this flag so a passive surface
// re-publish (same gate) cannot re-green the pill. The flag is cleared by a
// GENUINE change to the gate inputs (content mutation) or by a project switch.
let serverBlocked = false
// A monotonically increasing epoch bumped whenever the publish-readiness inputs
// genuinely change (content edit). The surface publisher records the epoch it
// last evaluated against; a server 'fix' only yields to a surface re-publish
// observed at a STRICTLY NEWER epoch than the one the 'fix' was pinned at.
let inputsEpoch = 0
let serverBlockedAtEpoch = -1

/** A monotonic run token. Callers capture one and only publish while latest. */
export function nextPublishReadinessRunToken(): number {
  return ++currentRunToken
}

/**
 * Signal that the publish-readiness gate inputs GENUINELY changed (a content
 * edit). Wired to the studio-store content subscriber that already markStale's
 * this source. Bumping the epoch is what lets a fresh surface estimate move the
 * pill off a sticky server 'fix' — a bare remount (no content change) does not
 * bump it, so the server rejection sticks (CA-1 = L5-C4).
 */
export function markPublishReadinessInputsChanged(): void {
  inputsEpoch += 1
}

/**
 * Reset the change-guard memo + sticky server-blocked flag. MUST be called on
 * project switch (when the verdict store is cleared): the store's entry is
 * wiped, so the next project's pre-flight must be allowed to publish even if it
 * derives the same signature the previous project last published.
 *
 * The run token counter is INTENTIONALLY left monotonic (NOT zeroed): zeroing
 * it made tokens reusable across projects, so a slow stale reflux from project A
 * could re-collide with a token re-issued for project B (L5-C1 = CA-3). Keeping
 * the counter strictly increasing guarantees project B always gets a newer
 * token than any of project A's in-flight captures.
 */
export function resetPublishReadinessVerdict(): void {
  lastPublished = null
  serverBlocked = false
  serverBlockedAtEpoch = -1
  // currentRunToken is deliberately NOT reset — see docstring (L5-C1/CA-3).
  // inputsEpoch is deliberately NOT reset either — it only needs monotonicity;
  // resetting the sticky `serverBlockedAtEpoch` to -1 already lets the next
  // project's first estimate publish freely.
}

/** Test-only: fully reset module state, including the monotonic token. */
export function __resetPublishReadinessVerdictForTest(): void {
  currentRunToken = 0
  lastPublished = null
  serverBlocked = false
  inputsEpoch = 0
  serverBlockedAtEpoch = -1
}

/** The humanized headline of the worst (first) reason, or null when ready. */
function worstCheckOf(
  gate: PublishReadinessGateLike,
  locale: LocaleCode,
): { worstCheck: string | null; rawReason: string | null } {
  const raw = gate.reasons[0] ?? null
  if (!raw) return { worstCheck: null, rawReason: null }
  // The HUMAN title (not the raw engineering string) — the pill drill-down and
  // tooltip read worstCheck, so it must be creator-readable.
  return { worstCheck: humanizePublishReason(raw, locale).title, rawReason: raw }
}

function signaturesEqual(a: PublishedSignature | null, b: PublishedSignature): boolean {
  return (
    a !== null &&
    a.verdict === b.verdict &&
    a.origin === b.origin &&
    a.locale === b.locale &&
    a.worstCheck === b.worstCheck &&
    a.rawReason === b.rawReason
  )
}

function entryMatchesPublishInput(
  entry: VerdictEntry | undefined,
  input: PublishVerdictInput,
): boolean {
  const counts = input.counts ?? { pass: 0, warn: 0, fail: 0 }
  return (
    entry !== undefined &&
    entry.source === input.source &&
    entry.label === input.label &&
    entry.verdict === input.verdict &&
    entry.score === (input.score ?? null) &&
    entry.counts.pass === counts.pass &&
    entry.counts.warn === counts.warn &&
    entry.counts.fail === counts.fail &&
    entry.worstCheck === (input.worstCheck ?? null) &&
    entry.scope === (input.scope ?? null) &&
    entry.staleSince === null &&
    (input.startedAt === undefined
      ? entry.startedAt === entry.finishedAt
      : entry.startedAt === input.startedAt) &&
    (input.finishedAt === undefined
      ? entry.finishedAt !== null
      : entry.finishedAt === input.finishedAt)
  )
}

/**
 * Publish and keep the private change guard consistent with the canonical
 * store. Zustand commits state before notifying subscribers, so a thrown
 * listener is a successful write with a failed notification. We recognize that
 * case only when the observed entry is a new reference and exactly matches the
 * payload this call intended to publish. A true pre-commit failure leaves both
 * the prior entry and `lastPublished` untouched and remains retryable.
 */
function publishCanonicalVerdict(
  input: PublishVerdictInput,
  signature: PublishedSignature,
): boolean {
  let previousEntry: VerdictEntry | undefined
  let writeAttempted = false

  try {
    const store = useValidationVerdictStore.getState()
    previousEntry = store.entries[SOURCE]
    writeAttempted = true
    store.publish(input)
  } catch {
    if (!writeAttempted) return false
    try {
      const observed = useValidationVerdictStore.getState().entries[SOURCE]
      if (
        observed === previousEntry ||
        !entryMatchesPublishInput(observed, input)
      ) {
        return false
      }
    } catch {
      return false
    }
  }

  lastPublished = signature
  return true
}

/**
 * Publish the publish-readiness pre-flight as an UNSCOPED 'publish-readiness'
 * verdict — but ONLY when the derived verdict/worstCheck CHANGED from the last
 * published value. Safe to call on every render (the surface does); the change
 * guard is what prevents the effect loop. Returns true when it actually wrote.
 *
 * Semantics (the redesign):
 *   - not-ready → 'review' (AMBER estimate, "not ship-ready yet") — NEVER 'fix'.
 *     A still-building draft is not broken, so it must not pin the pill red (R1).
 *   - ready     → 'ok' (green pre-flight estimate; copy frames it honestly).
 *
 * The server reflux STICKS (CA-1 = L5-C4): once `serverBlocked` is pinned, a
 * surface re-publish observed at the SAME inputs epoch the 'fix' was pinned at
 * is suppressed — only a genuine content change (newer epoch) lets the surface
 * estimate move the pill again.
 *
 * `token` (optional) gates the write under the shared run-token; omit it for the
 * synchronous surface path (the surface relies on the change guard + sticky
 * flag, not the token, since it has no async clobber risk of its own).
 */
export function publishReadinessVerdict(
  gate: PublishReadinessGateLike,
  locale: LocaleCode,
  token?: number,
): boolean {
  if (token !== undefined && token !== currentRunToken) return false

  // The server's authoritative 'fix' sticks until the gate inputs genuinely
  // change. A passive surface re-render (same epoch) must NOT re-green the pill.
  if (serverBlocked && inputsEpoch <= serverBlockedAtEpoch) return false
  // A genuine content change happened since the server blocked → the estimate is
  // allowed to supersede; clear the sticky flag and re-evaluate fresh.
  if (serverBlocked && inputsEpoch > serverBlockedAtEpoch) {
    serverBlocked = false
    serverBlockedAtEpoch = -1
  }

  const verdict: 'ok' | 'review' = gate.publishReady ? 'ok' : 'review'
  const { worstCheck, rawReason } = gate.publishReady
    ? { worstCheck: null, rawReason: null }
    : worstCheckOf(gate, locale)

  const signature: PublishedSignature = {
    verdict,
    origin: 'surface',
    locale,
    worstCheck,
    rawReason,
  }
  // Change guard: no write when nothing the pill cares about changed.
  if (signaturesEqual(lastPublished, signature)) return false

  // Count the distinct blocking reasons as fails (0 when ready).
  const fail = gate.publishReady ? 0 : new Set(gate.reasons).size

  return publishCanonicalVerdict(
    {
      source: SOURCE,
      label: LABEL,
      verdict,
      counts: { pass: 0, warn: 0, fail },
      worstCheck,
      // UNSCOPED — whole-app pre-flight estimate (see module header).
      scope: null,
    },
    signature,
  )
}

/**
 * Relocalize only the presentation of the already-published verdict.
 *
 * Zustand commits state before notifying subscribers. If a real subscriber
 * throws, `setState` throws even though the replacement entry already landed.
 * In that post-commit case we reconcile the private signature to the observed
 * committed entry and truthfully return true. Pre-commit, lookup, and
 * humanization failures leave the private signature unchanged and return false.
 */
export function relocalizePublishedReadinessVerdict(
  locale: LocaleCode,
): boolean {
  try {
    const signature = lastPublished
    const current = useValidationVerdictStore.getState().entries[SOURCE]
    if (!signature || !current) return false

    const worstCheck = signature.rawReason
      ? humanizePublishReason(signature.rawReason, locale).title
      : current.worstCheck

    if (signature.locale === locale && current.worstCheck === worstCheck) {
      return false
    }

    const nextEntry = { ...current, worstCheck }
    const nextSignature = { ...signature, locale, worstCheck }
    try {
      useValidationVerdictStore.setState((state) => ({
        entries: {
          ...state.entries,
          [SOURCE]: nextEntry,
        },
      }))
    } catch {
      const observed = useValidationVerdictStore.getState().entries[SOURCE]
      if (observed !== nextEntry) return false
    }

    lastPublished = nextSignature
    return true
  } catch {
    return false
  }
}

/**
 * Reflux a SERVER gate rejection ('Publishing blocked') into the SAME canonical
 * 'publish-readiness' source as an AUTHORITATIVE 'fix'. This is the key fix for
 * the green-precedes-rejection split: when publishProject throws
 * `{ gateResponse: { ok:false, reasons } }`, the authoritative server verdict
 * flows into the store the app-health pill reads, so a surface estimate can no
 * longer survive a server 'blocked'.
 *
 * The server gate has STRICTER inputs than the client estimate — the reflux
 * makes the store reflect the server's authoritative answer and PINS a sticky
 * flag so a passive surface re-render cannot immediately re-green the pill
 * (CA-1 = L5-C4). The flag clears only when the gate inputs genuinely change.
 *
 * Pass the run token captured at publish-start so a slow rejection from an
 * earlier attempt cannot clobber a newer verdict (the token counter is
 * monotonic, so this also blocks a cross-project stale reflux — L5-C1/CA-3).
 */
export function refluxServerGateRejection(
  reasons: readonly string[] | undefined,
  token?: number,
): boolean {
  if (token !== undefined && token !== currentRunToken) return false

  const list = reasons ?? []
  const raw = list[0] ?? null
  const worstCheck = raw ? humanizePublishReason(raw, 'en').title : 'Publishing was blocked by the quality gate'
  const fail = list.length > 0 ? new Set(list).size : 1

  const signature: PublishedSignature = {
    verdict: 'fix',
    origin: 'server',
    locale: 'en',
    worstCheck,
    rawReason: raw,
  }

  // Pin the sticky flag at the CURRENT inputs epoch regardless of whether the
  // store write is skipped as a no-op below: a duplicate reflux must still
  // re-arm stickiness against the latest epoch so a content edit that landed
  // between two identical rejections does not silently un-stick the pill.
  serverBlocked = true
  serverBlockedAtEpoch = inputsEpoch

  // A reflux ALWAYS lands (it carries the authoritative server answer); skip the
  // write only when it is byte-identical to what is already published so the
  // store does not churn under rapid retries.
  if (signaturesEqual(lastPublished, signature)) return false

  return publishCanonicalVerdict(
    {
      source: SOURCE,
      label: LABEL,
      verdict: 'fix',
      counts: { pass: 0, warn: 0, fail },
      worstCheck,
      scope: null,
    },
    signature,
  )
}
